import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import StickyBox from 'react-sticky-box';
import Breadcrumb from '../Breadcrumb';
import Sidebar from '../Sidebar';
import axios from 'axios';
import MetadataReview, { mergeMetadataSuggestions } from './MetadataReview';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { Api } from '../../auth/Configs';
import {
  ARCHIVE_PROFILES,
  GED_DOCUMENT_ADD_VERSION,
  GED_DOCUMENT_CREATE,
  GED_DOCUMENT_DETAIL,
  GED_DOCUMENT_DETAIL_UUID,
  GED_OCR_PREVIEW,
  GED_DOCUMENT_UPDATE,
  PLAN_FOLDERS,
  PLAN_META_LIST,
  REGLES_CONSERVATION,
} from '../../auth/urlParameters';
import { ToastConfirm, ToastError } from '../../../ui/AlertesUi';
import './ArchiveCreate.css';
import { getAssignedServiceIds } from './serviceScope';

const defaultForm = {
  planId: '',
  folderId: '',
  title: '',
  reference: '',
  archiveProfileId: '',
  regleConservationId: '',
  retentionEventDate: '',
  producerServiceId: '',
};

const flattenServices = (nodes, depth = 0) =>
  (nodes || []).reduce((acc, node) => {
    acc.push({
      id: node.id,
      label: `${'-- '.repeat(depth)}${node.code} - ${node.title}`,
    });
    if (node.children && node.children.length > 0) {
      acc.push(...flattenServices(node.children, depth + 1));
    }
    return acc;
  }, []);

const flattenFolders = (nodes, depth = 0, ancestorIds = []) =>
  (nodes || []).reduce((acc, node) => {
    acc.push({
      id: node.id,
      planId: node.planId || null,
      ancestorIds,
      label: `${'-- '.repeat(depth)}${node.reference} - ${node.title}`,
    });
    if (node.children && node.children.length > 0) {
      acc.push(...flattenFolders(node.children, depth + 1, [...ancestorIds, Number(node.id)]));
    }
    return acc;
  }, []);

const sortPlans = (a, b) => {
  const codeA = (a.code || '').toString();
  const codeB = (b.code || '').toString();
  if (codeA && codeB && codeA !== codeB) {
    return codeA.localeCompare(codeB);
  }
  return (a.title || '').toString().localeCompare((b.title || '').toString());
};

const buildPlanTree = (plans) => {
  const byId = new Map();
  (plans || []).forEach((plan) => {
    byId.set(Number(plan.id), { ...plan, children: [] });
  });
  const roots = [];
  byId.forEach((node) => {
    const parentId = node.parentId ? Number(node.parentId) : null;
    if (parentId && byId.has(parentId)) {
      byId.get(parentId).children.push(node);
    } else {
      roots.push(node);
    }
  });
  const sortNode = (node) => {
    node.children.sort(sortPlans);
    node.children.forEach(sortNode);
  };
  roots.sort(sortPlans);
  roots.forEach(sortNode);
  return roots;
};

const flattenPlanTree = (nodes, depth = 0) =>
  (nodes || []).reduce((acc, node) => {
    acc.push({ plan: node, depth });
    if (node.children && node.children.length > 0) {
      acc.push(...flattenPlanTree(node.children, depth + 1));
    }
    return acc;
  }, []);

const slugifyCode = (value) =>
  (value || '')
    .toString()
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_')
    .replace(/[^A-Z0-9_]/g, '')
    .replace(/_+/g, '_');

const ArchiveCreate = () => {
  const session = useSelector((state) => state.sessionAuth);
  const profile = useSelector((state) => state.userSettings);
  const navigate = useNavigate();
  const location = useLocation();
  const [plans, setPlans] = useState([]);
  const [folders, setFolders] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [regles, setRegles] = useState([]);
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [addingService, setAddingService] = useState(false);
  const [newService, setNewService] = useState({ code: '', title: '' });
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [archiveFile, setArchiveFile] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showArchiveMetadata, setShowArchiveMetadata] = useState(false);
  const [docLoaded, setDocLoaded] = useState(false);
  const [docInfo, setDocInfo] = useState(null);
  const [ocrPreview, setOcrPreview] = useState(null);
  const [captureLogs, setCaptureLogs] = useState([]);
  const [reviewedMetadata, setReviewedMetadata] = useState({});
  const protectedMetadata = useRef({});
  const captureGeneration = useRef(0);
  const [ocrOverrides, setOcrOverrides] = useState({});
  const [ocrPreviewLoading, setOcrPreviewLoading] = useState(false);
  const [ocrPreviewError, setOcrPreviewError] = useState('');
  const [customMetadataFields, setCustomMetadataFields] = useState([{ key: '', value: '' }]);

  const roles = useMemo(() => Array.isArray(session.roles) ? session.roles : [], [session.roles]);
  const isAssistantOnly =
    roles.includes('ROLE_ASSIST')
    && !roles.some((role) =>
      ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_EDITED'].includes(role)
    );
  const canAddService = roles.some((role) =>
    ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_EDITED'].includes(role)
  );
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const docUuid = useMemo(() => {
    const value = queryParams.get('docUuid');
    return value ? String(value) : null;
  }, [queryParams]);
  const legacyDocId = useMemo(() => {
    const value = queryParams.get('docId');
    if (!value) return null;
    return /^\d+$/.test(value) ? Number(value) : null;
  }, [queryParams]);
  const isEdit = Boolean(docUuid || legacyDocId);
  const requestedPlanId = useMemo(() => {
    const value = queryParams.get('planId');
    return value ? Number(value) : null;
  }, [queryParams]);
  const requestedFolderId = useMemo(() => {
    const value = queryParams.get('folderId');
    return value ? Number(value) : null;
  }, [queryParams]);
  const manualChoices = useRef({ producerServiceId: false });
  const appliedRequestedFolder = useRef(null);
  const accessFilters = profile?.accessFilters || {};
  const planScopeIds = useMemo(
    () => (Array.isArray(accessFilters.planIds) ? accessFilters.planIds.map(Number) : []),
    [accessFilters.planIds]
  );
  const folderScopeIds = useMemo(
    () => (Array.isArray(accessFilters.folderIds) ? accessFilters.folderIds.map(Number) : []),
    [accessFilters.folderIds]
  );
  const serviceScopeIds = useMemo(() => getAssignedServiceIds(profile), [profile]);
  const hasPlanScope = planScopeIds.length > 0;
  const hasFolderScope = folderScopeIds.length > 0;
  const userServiceId = profile?.serviceUnit?.id ? String(profile.serviceUnit.id) : '';

  const authHeaders = useMemo(
    () => ({
      headers: { Authorization: 'Bearer ' + session.token },
    }),
    [session.token]
  );

  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles[0] || null;
    if (!file) return;
    setArchiveFile(file);
    setValidationErrors((previous) => ({ ...previous, archiveFile: '' }));
    const suggestedTitle = file.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim();
    setForm((previous) => ({
      ...previous,
      title: previous.title.trim() ? previous.title : suggestedTitle,
    }));
  };

  const { fileRejections, getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    onDrop,
  });

  const fileRejectionItems = fileRejections.map(({ file, errors }) => (
    <li key={file.path}>
      <div className="alert alert-danger alert-dismissible fade show mt-3" role="alert">
        {file.path} - {file.size} bytes
        <ul>
          {errors.map((e) => (
            <li key={e.code}>{e.message}</li>
          ))}
        </ul>
        <button type="button" className="close" data-dismiss="alert" aria-label="Close">
          <span aria-hidden="true">×</span>
        </button>
      </div>
    </li>
  ));

  useEffect(() => {
    axios
      .get(ARCHIVE_PROFILES(), {
        ...authHeaders,
        params: { status: 'active', available: 1, limit: 100 },
      })
      .then((res) => {
        setProfiles(res.data?.profiles || []);
      })
      .catch(() => setProfiles([]));
  }, [authHeaders]);

  useEffect(() => {
    axios
      .get(REGLES_CONSERVATION(), authHeaders)
      .then((res) => {
        setRegles(res.data?.regles || []);
      })
      .catch(() => setRegles([]));
  }, [authHeaders]);

  useEffect(() => {
    setLoadingServices(true);
    axios
      .get(`${Api}/services`, authHeaders)
      .then((res) => {
        const flat = flattenServices(res.data?.services || []);
        setServices(flat);
      })
      .catch(() => setServices([]))
      .finally(() => setLoadingServices(false));
  }, [authHeaders]);

  useEffect(() => {
    if (isAssistantOnly && userServiceId && serviceScopeIds.length === 1) {
      setForm((prev) => ({ ...prev, producerServiceId: userServiceId }));
    }
  }, [isAssistantOnly, userServiceId, serviceScopeIds]);

  useEffect(() => {
    axios
      .get(PLAN_META_LIST(), authHeaders)
      .then((res) => {
        const list = res.data?.plans || [];
        setPlans(list);
      })
      .catch(() => setPlans([]));
  }, [authHeaders]);

  const scopedFolders = useMemo(
    () =>
      folders.filter(
        (folder) =>
          (!hasPlanScope || planScopeIds.includes(Number(folder.planId))) &&
          (!hasFolderScope || folderScopeIds.includes(Number(folder.id)) ||
            folder.ancestorIds.some((id) => folderScopeIds.includes(id)))
      ),
    [folders, hasPlanScope, planScopeIds, hasFolderScope, folderScopeIds]
  );

  const availablePlans = useMemo(() => {
    let list = isEdit ? plans : plans.filter((plan) => plan.active);
    if (hasPlanScope) {
      list = list.filter((plan) => planScopeIds.includes(Number(plan.id)));
    }
    if (hasFolderScope) {
      const allowedPlanIds = new Set(scopedFolders.map((folder) => Number(folder.planId)));
      if (allowedPlanIds.size > 0) {
        list = list.filter((plan) => allowedPlanIds.has(Number(plan.id)));
      } else {
        list = [];
      }
    }
    return list;
  }, [
    plans,
    isEdit,
    scopedFolders,
    hasPlanScope,
    planScopeIds,
    hasFolderScope,
  ]);

  const groupedPlanOptions = useMemo(
    () => flattenPlanTree(buildPlanTree(availablePlans)),
    [availablePlans]
  );

  useEffect(() => {
    if (availablePlans.length === 0) {
      setForm((prev) => ({ ...prev, planId: '', folderId: '' }));
      return;
    }
    if (isEdit && docLoaded) {
      return;
    }
    const allowed = availablePlans.some(
      (plan) => Number(plan.id) === Number(form.planId)
    );
    if (!form.planId || !allowed) {
      const requestedAllowed = requestedPlanId
        && availablePlans.some((plan) => Number(plan.id) === Number(requestedPlanId));
      const nextPlanId = requestedAllowed ? requestedPlanId : availablePlans[0].id;
      setForm((prev) => ({ ...prev, planId: nextPlanId, folderId: '' }));
    }
  }, [availablePlans, form.planId, requestedPlanId, docLoaded, isEdit]);

  useEffect(() => {
    axios
      .get(PLAN_FOLDERS(), authHeaders)
      .then((res) => {
        const flat = flattenFolders(res.data?.folders || []);
        setFolders(flat);
      })
      .catch(() => setFolders([]));
  }, [authHeaders]);

  const filteredFolders = useMemo(() => {
    if (!form.planId) return [];
    return scopedFolders.filter((folder) => Number(folder.planId) === Number(form.planId));
  }, [scopedFolders, form.planId]);

  useEffect(() => {
    if (!requestedFolderId) return;
    if (appliedRequestedFolder.current === requestedFolderId) return;
    if (!form.planId) return;
    if (form.folderId) return;
    const match = filteredFolders.find(
      (folder) => Number(folder.id) === Number(requestedFolderId)
    );
    if (match) {
      appliedRequestedFolder.current = requestedFolderId;
      setForm((prev) => ({ ...prev, folderId: String(match.id) }));
    }
  }, [filteredFolders, form.planId, form.folderId, requestedFolderId]);

  useEffect(() => {
    const targetUuid = docUuid;
    const targetId = legacyDocId;
    if (!targetUuid && !targetId) return;
    let isMounted = true;
    setDocLoaded(false);
    const request = targetUuid
      ? axios.get(GED_DOCUMENT_DETAIL_UUID(targetUuid), authHeaders)
      : axios.get(GED_DOCUMENT_DETAIL(targetId), authHeaders);
    request
      .then((res) => {
        if (!isMounted) return;
        const data = res.data || {};
        setForm((prev) => ({
          ...prev,
          planId: data.planId ? String(data.planId) : '',
          folderId: data.folderId ? String(data.folderId) : '',
          title: data.title || '',
          reference: data.reference || '',
          archiveProfileId: data.archiveProfileId ? String(data.archiveProfileId) : '',
          regleConservationId: data.regleConservationId ? String(data.regleConservationId) : '',
          retentionEventDate: data.retentionStartAt || '',
          producerServiceId: data.producerServiceId ? String(data.producerServiceId) : prev.producerServiceId,
        }));
        manualChoices.current = { producerServiceId: true };
        setDocInfo(data);
        if (!targetUuid && data.uuid) {
          navigate(`/apps/in/archives/add?docUuid=${data.uuid}`, { replace: true });
        }
      })
      .catch(() => {
        if (!isMounted) return;
        ToastError('Impossible de charger le document');
      })
      .finally(() => {
        if (isMounted) setDocLoaded(true);
      });
    return () => {
      isMounted = false;
    };
  }, [authHeaders, docUuid, legacyDocId, navigate]);

  useEffect(() => {
    captureGeneration.current += 1;
    protectedMetadata.current = {};
    setReviewedMetadata({});
    setCaptureLogs([]);
    setOcrPreviewLoading(false);
    if (isEdit && docInfo && !archiveFile
      && String(form.archiveProfileId) === String(docInfo.archiveProfileId)) {
      setOcrPreview(null);
      setOcrOverrides(docInfo.metadata || {});
      protectedMetadata.current = Object.fromEntries(Object.keys(docInfo.metadata || {}).map((key) => [key, true]));
      setOcrPreviewError('');
      return;
    }
    setOcrPreview(null);
    setOcrOverrides({});
    setOcrPreviewError('');
  }, [form.archiveProfileId, archiveFile, isEdit, docInfo]);

  const canCreateArchive = useMemo(() => {
    const canRole = roles.some((role) =>
      ['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_EDITED', 'ROLE_ASSIST'].includes(role)
    );
    if (!canRole) return false;
    if (!form.planId) return false;
    if (roles.includes('ROLE_ASSIST') && hasPlanScope) {
      return planScopeIds.includes(Number(form.planId));
    }
    return true;
  }, [roles, form.planId, hasPlanScope, planScopeIds]);

  const selectedProfile = useMemo(() => {
    const profileId = Number(form.archiveProfileId);
    if (!profileId) return null;
    return profiles.find((profile) => Number(profile.id) === profileId) || null;
  }, [profiles, form.archiveProfileId]);

  const captureFields = useMemo(() => {
    if (!selectedProfile || !Array.isArray(selectedProfile.captureFields)) return [];
    return selectedProfile.captureFields;
  }, [selectedProfile]);

  useEffect(() => {
    if (!selectedProfile || isEdit) return;
    setForm((previous) => {
      const defaultServiceId = selectedProfile.producerServiceId;
      const nextServiceId = !isAssistantOnly && !addingService && !manualChoices.current.producerServiceId && defaultServiceId
        && services.some((service) => String(service.id) === String(defaultServiceId))
        ? String(defaultServiceId) : previous.producerServiceId;
      if (String(nextServiceId) === String(previous.producerServiceId)) return previous;
      return {
        ...previous,
        producerServiceId: nextServiceId,
      };
    });
  }, [selectedProfile, services, isEdit, isAssistantOnly, addingService]);

  const chooseProfile = (archiveProfileId) => {
    const nextProfile = profiles.find((item) => String(item.id) === archiveProfileId);
    setForm((previous) => {
      const defaultServiceId = nextProfile?.producerServiceId;
      const allowedDefaultService = defaultServiceId && services.some((service) => String(service.id) === String(defaultServiceId));
      return {
        ...previous,
        archiveProfileId,
        producerServiceId: !isAssistantOnly && !addingService && !manualChoices.current.producerServiceId && allowedDefaultService
          ? String(defaultServiceId) : previous.producerServiceId,
      };
    });
    setValidationErrors((previous) => ({ ...previous, archiveProfileId: '' }));
  };
  const hasValue = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim() !== '';
    if (Array.isArray(value)) return value.length > 0;
    return true;
  };


  const coerceOcrValue = (field, value) => {
    if (!hasValue(value)) return null;
    const type = (field?.type || 'string').toLowerCase();
    if (type === 'number') {
      const normalized = String(value).replace(',', '.');
      const parsed = Number(normalized);
      return Number.isNaN(parsed) ? value : parsed;
    }
    if (type === 'boolean') {
      const lower = String(value).toLowerCase();
      if (['1', 'true', 'oui', 'yes'].includes(lower)) return true;
      if (['0', 'false', 'non', 'no'].includes(lower)) return false;
      return value;
    }
    return value;
  };

  const buildOcrMetadataOverrides = () => {
    if (!captureFields.length || !ocrOverrides) return null;
    const metadata = {};
    captureFields.forEach((field) => {
      const key = field?.key;
      if (!key) return;
      const rawValue = ocrOverrides[key];
      if (!hasValue(rawValue)) return;
      metadata[key] = coerceOcrValue(field, rawValue);
    });
    return Object.keys(metadata).length > 0 ? metadata : null;
  };

  const updateCustomMetadataField = (index, field, value) => {
    setCustomMetadataFields((prev) =>
      prev.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item
      )
    );
  };

  const addCustomMetadataField = () => {
    setCustomMetadataFields((prev) => [...prev, { key: '', value: '' }]);
  };

  const removeCustomMetadataField = (index) => {
    setCustomMetadataFields((prev) =>
      prev.length === 1 ? [{ key: '', value: '' }] : prev.filter((_, itemIndex) => itemIndex !== index)
    );
  };

  const buildCustomMetadata = () => {
    if (!Array.isArray(customMetadataFields) || customMetadataFields.length === 0) {
      return null;
    }
    const metadata = {};
    customMetadataFields.forEach((field) => {
      const key = typeof field?.key === 'string' ? field.key.trim() : '';
      if (!key) return;
      const rawValue = field?.value;
      if (!hasValue(rawValue)) return;
      metadata[key] = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
    });
    return Object.keys(metadata).length > 0 ? metadata : null;
  };

  const createService = async () => {
    const title = newService.title.trim();
    const code = newService.code.trim() || slugifyCode(title);
    if (!title) {
      throw new Error('Libellé du service requis.');
    }
    if (!code) {
      throw new Error('Code du service requis.');
    }
    const response = await axios.post(
      `${Api}/services`,
      { code, title },
      authHeaders
    );
    const createdId = response.data?.id ? String(response.data.id) : '';
    const listRes = await axios.get(`${Api}/services`, authHeaders);
    setServices(flattenServices(listRes.data?.services || []));
    setNewService({ code: '', title: '' });
    setAddingService(false);
    return createdId;
  };

  const handlePreviewOcr = async () => {
    if (!archiveFile) {
      ToastError('Ajoutez un fichier à archiver');
      return;
    }
    if (!form.archiveProfileId) {
      ToastError('Sélectionnez un profil d\'archive');
      return;
    }

    const generation = ++captureGeneration.current;
    setOcrPreviewLoading(true);
    setOcrPreviewError('');
    try {
      const formData = new FormData();
      formData.append('file', archiveFile);
      formData.append('archiveProfileId', Number(form.archiveProfileId));
      const response = await axios.post(GED_OCR_PREVIEW(), formData, {
        headers: { Authorization: 'Bearer ' + session.token },
      });
      if (generation !== captureGeneration.current) return;
      const metadata = response.data?.metadata || {};
      setOcrPreview(metadata);
      setCaptureLogs(response.data?.captureLogs || []);
      setOcrOverrides((current) => mergeMetadataSuggestions(current, metadata, protectedMetadata.current));
      if (Object.keys(metadata).length === 0) {
        setOcrPreviewError('Aucune métadonnée OCR détectée.');
      }
    } catch (err) {
      if (generation !== captureGeneration.current) return;
      setOcrPreviewError(err.response?.data?.message || 'Impossible de prévisualiser l\'OCR');
    } finally {
      if (generation === captureGeneration.current) setOcrPreviewLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canCreateArchive) {
      ToastError('Accès insuffisant pour créer une archive');
      return;
    }
    const errors = {};
    if (!form.title.trim()) errors.title = 'Indiquez un titre.';
    if (!form.archiveProfileId) errors.archiveProfileId = 'Choisissez un profil d’archive.';
    if (!form.planId) errors.planId = 'Choisissez un plan de classement.';
    if (!form.regleConservationId) errors.regleConservationId = 'Choisissez une règle de conservation.';
    if (regles.find((item) => String(item.id) === String(form.regleConservationId))?.retentionTrigger === 'event' && !form.retentionEventDate) errors.retentionEventDate = 'Indiquez la date de l’événement déclencheur.';
    if (addingService && !newService.title.trim()) {
      errors.producerServiceId = 'Indiquez le libellé du nouveau service.';
    } else if (!addingService && !form.producerServiceId && !(isAssistantOnly && userServiceId && serviceScopeIds.length === 1)) {
      errors.producerServiceId = 'Choisissez un service producteur.';
    }
    if (!isEdit && !archiveFile) errors.archiveFile = 'Ajoutez un fichier à archiver.';
    const missingRequiredFields = captureFields.filter((field) => {
      if (!field?.required || !field?.key) return false;
      return !hasValue(ocrOverrides?.[field.key]);
    });
    missingRequiredFields.forEach((field) => {
      errors[`metadata:${field.key}`] = 'Cette métadonnée est obligatoire.';
    });
    if (archiveFile && selectedProfile) {
      const maxFileSizeMb = Number(selectedProfile.maxFileSizeMb || 0);
      if (maxFileSizeMb > 0 && archiveFile.size > maxFileSizeMb * 1024 * 1024) {
        errors.archiveFile = `Le fichier dépasse la taille maximale de ${maxFileSizeMb} Mo.`;
      }
      const allowedFormats = Array.isArray(selectedProfile.allowedFormats)
        ? selectedProfile.allowedFormats.map((format) => String(format).toLowerCase())
        : [];
      if (allowedFormats.length > 0) {
        const extension = archiveFile.name.includes('.')
          ? archiveFile.name.split('.').pop().toLowerCase()
          : '';
        const acceptedFormats = allowedFormats.includes('pdfa')
          ? [...allowedFormats, 'pdf']
          : allowedFormats;
        if (!extension || !acceptedFormats.includes(extension)) {
          errors.archiveFile = `Format non autorisé. Formats acceptés : ${allowedFormats.join(', ')}.`;
        }
      }
    }
    setValidationErrors(errors);
    if (Object.keys(errors).some((key) => key.startsWith('metadata:'))) setShowArchiveMetadata(true);
    if (Object.keys(errors).length > 0) {
      const firstField = Object.keys(errors)[0];
      const section = firstField.startsWith('metadata:') || firstField === 'archiveProfileId'
        ? 'verification' : ['title', 'archiveFile'].includes(firstField) ? 'document' : 'classement';
      document.getElementById(`archive-create-${section}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    if (isEdit && !docInfo?.id) {
      ToastError('Impossible de charger le document');
      return;
    }

    setSaving(true);
    const createRequest = async () => {
      const editId = isEdit ? docInfo?.id : null;
      let resolvedServiceId = form.producerServiceId;
      if (isAssistantOnly && userServiceId && serviceScopeIds.length === 1) {
        resolvedServiceId = userServiceId;
      } else if (addingService) {
        resolvedServiceId = await createService();
      }

      if (!resolvedServiceId) {
        ToastError('Sélectionnez un service producteur');
        setSaving(false);
        return;
      }

      const payload = {
        planId: Number(form.planId),
        title: form.title.trim(),
        archiveProfileId: Number(form.archiveProfileId),
        regleConservationId: Number(form.regleConservationId),
        producerServiceId: Number(resolvedServiceId),
      };
      if (form.retentionEventDate) payload.retentionEventDate = form.retentionEventDate;

      const ocrMetadata = buildOcrMetadataOverrides();
      const customMetadata = buildCustomMetadata();
      if (isEdit && customMetadata) payload.customMetadata = customMetadata;
      const metadataPayload = {
        ...(customMetadata || {}),
        ...(ocrMetadata || {}),
      };
      if (Object.keys(metadataPayload).length > 0) {
        payload.metadata = metadataPayload;
      }
      payload.verifiedMetadataKeys = Object.keys(reviewedMetadata).filter((key) => reviewedMetadata[key] && Object.prototype.hasOwnProperty.call(metadataPayload, key));

      if (form.folderId) {
        payload.folderId = Number(form.folderId);
      }

      if (form.reference.trim()) {
        payload.reference = form.reference.trim();
      }

      const createRes = isEdit
        ? await axios.patch(GED_DOCUMENT_UPDATE(editId), payload, authHeaders)
        : await axios.post(GED_DOCUMENT_CREATE(), payload, authHeaders);
      const createdId = isEdit ? editId : createRes.data?.id;
      if (!createdId) {
        throw new Error('Création d\'archive incomplète');
      }
      if (archiveFile) {
        const formData = new FormData();
        formData.append('file', archiveFile);
        await axios.post(
          GED_DOCUMENT_ADD_VERSION(createdId),
          formData,
          {
            headers: { Authorization: 'Bearer ' + session.token },
          }
        );
         
      }
     
      ToastConfirm(isEdit ? 'Archive mise à jour' : 'Archive créée');
      navigate('/apps/in/archives');
    };

    createRequest()
      .catch((err) => {
        ToastError(err.response?.data?.message || err.message || 'Erreur de création d\'archive');
      })
      .finally(() => setSaving(false));
  };

  return (
    <Fragment>
      <Breadcrumb title="Archives" submenu="Nouvelle archive" />
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-4 col-lg-3 col-xl-3 theiaStickySidebar">
              <StickyBox offsetTop={20} offsetBottom={20}>
                <Sidebar />
              </StickyBox>
            </div>
            <div className="col-md-8 col-lg-9 col-xl-9">
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <h4 className="htitle mb-0">
                      {isEdit ? 'Modifier une archive' : 'Créer une archive'}
                    </h4>
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => navigate('/apps/in/archives')}
                    >
                      Retour à la liste
                    </button>
                  </div>

                  <form onSubmit={handleSubmit} noValidate className="archive-create-form">
                    <section id="archive-create-document" className="archive-create-section">
                      <div className="archive-create-section-heading">
                        <span className="archive-create-step">1</span>
                        <div><h5>Document</h5><p>Choisissez le fichier et renseignez son titre.</p></div>
                      </div>
                      <div className="archive-create-section-body">
                        <div className="form-group">
                          <label>Fichier à archiver {!isEdit && <span className="archive-create-required">*</span>}</label>
                          {isEdit && docInfo?.latestVersion?.originalName && (
                            <p className="archive-create-hint">Fichier actuel : {docInfo.latestVersion.originalName}</p>
                          )}
                          <div {...getRootProps({ className: 'archive-create-dropzone' })}>
                            <input {...getInputProps()} />
                            <span className="archive-create-dropzone-icon" aria-hidden="true">↑</span>
                            <strong>Glissez un fichier ici ou cliquez pour le choisir</strong>
                            <span>Un seul document à la fois</span>
                          </div>
                          {archiveFile && (
                            <div className="archive-create-file">
                              <span><strong>{archiveFile.name}</strong> · {(archiveFile.size / 1024 / 1024).toFixed(2)} Mo</span>
                              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setArchiveFile(null)}>Retirer</button>
                            </div>
                          )}
                          {fileRejections.length > 0 && <ul className="dropzone_error">{fileRejectionItems}</ul>}
                          {validationErrors.archiveFile && <div className="archive-create-error" role="alert">{validationErrors.archiveFile}</div>}
                          {selectedProfile && (
                            <small className="archive-create-hint">
                              {selectedProfile.allowedFormats?.length > 0 && `Formats acceptés : ${selectedProfile.allowedFormats.join(', ')}. `}
                              {Number(selectedProfile.maxFileSizeMb) > 0 && `Taille maximale : ${selectedProfile.maxFileSizeMb} Mo.`}
                            </small>
                          )}
                        </div>
                        <div className="form-group">
                          <label htmlFor="archive-title">Titre <span className="archive-create-required">*</span></label>
                          <input id="archive-title" className="form-control" value={form.title}
                            onChange={(e) => { setForm((prev) => ({ ...prev, title: e.target.value })); setValidationErrors((prev) => ({ ...prev, title: '' })); }}
                            aria-invalid={!!validationErrors.title} />
                          <small className="archive-create-hint">Proposé à partir du nom du fichier. Vous pouvez le modifier.</small>
                          {validationErrors.title && <div className="archive-create-error" role="alert">{validationErrors.title}</div>}
                        </div>
                      </div>
                    </section>

                    <section id="archive-create-classement" className="archive-create-section">
                      <div className="archive-create-section-heading">
                        <span className="archive-create-step">2</span>
                        <div><h5>Classement</h5><p>Choisissez le service producteur, le plan et la destination.</p></div>
                      </div>
                      <div className="archive-create-section-body">
                        <div className="row">
                          <div className="col-md-6 form-group">
                            <label htmlFor="archive-service">Service producteur <span className="archive-create-required">*</span></label>
                            <select id="archive-service" className="form-control" value={form.producerServiceId}
                              onChange={(e) => { manualChoices.current.producerServiceId = true; setForm((prev) => ({ ...prev, producerServiceId: e.target.value })); setValidationErrors((prev) => ({ ...prev, producerServiceId: '' })); }}
                              disabled={(isAssistantOnly && serviceScopeIds.length === 1) || addingService || loadingServices}
                              aria-invalid={!!validationErrors.producerServiceId}>
                              <option value="">{loadingServices ? 'Chargement...' : 'Choisir un service'}</option>
                              {services.filter((service) => !isAssistantOnly || serviceScopeIds.includes(Number(service.id))).map((service) => <option key={service.id} value={service.id}>{service.label}</option>)}
                            </select>
                            {isAssistantOnly && serviceScopeIds.length > 0 && <small className="archive-create-hint">Choisissez l’un de vos services affectés.</small>}
                            {canAddService && !isAssistantOnly && (
                              <label className="archive-create-check">
                                <input type="checkbox" checked={addingService} onChange={(e) => {
                                  setAddingService(e.target.checked);
                                  if (e.target.checked) { manualChoices.current.producerServiceId = true; setForm((prev) => ({ ...prev, producerServiceId: '' })); }
                                  else setNewService({ code: '', title: '' });
                                }} />
                                Ajouter un nouveau service
                              </label>
                            )}
                            {addingService && !isAssistantOnly && (
                              <div className="row">
                                <div className="col-sm-4 form-group"><input className="form-control" placeholder="Code (auto)" value={newService.code}
                                  onChange={(e) => setNewService((prev) => ({ ...prev, code: e.target.value }))} /></div>
                                <div className="col-sm-8 form-group"><input className="form-control" placeholder="Libellé du service" value={newService.title}
                                  onChange={(e) => { setNewService((prev) => ({ ...prev, title: e.target.value })); setValidationErrors((prev) => ({ ...prev, producerServiceId: '' })); }} /></div>
                              </div>
                            )}
                            {validationErrors.producerServiceId && <div className="archive-create-error" role="alert">{validationErrors.producerServiceId}</div>}
                          </div>
                          <div className="col-md-6 form-group">
                            <label htmlFor="archive-plan">Plan de classement <span className="archive-create-required">*</span></label>
                            <select id="archive-plan" className="form-control" value={form.planId}
                              onChange={(e) => { setForm((prev) => ({ ...prev, planId: e.target.value, folderId: '' })); setValidationErrors((prev) => ({ ...prev, planId: '' })); }}
                              disabled={availablePlans.length === 0} aria-invalid={!!validationErrors.planId}>
                              <option value="">{availablePlans.length === 0 ? 'Aucun plan actif autorisé' : 'Choisir un plan'}</option>
                              {groupedPlanOptions.map(({ plan, depth }) => <option key={plan.id} value={plan.id}>{`${'-- '.repeat(depth)}${plan.code} - ${plan.title}`}</option>)}
                            </select>
                            {validationErrors.planId && <div className="archive-create-error" role="alert">{validationErrors.planId}</div>}
                          </div>
                          <div className="col-md-6 form-group">
                            <label htmlFor="archive-folder">Destination dans le plan</label>
                            <select id="archive-folder" className="form-control" value={form.folderId}
                              onChange={(e) => setForm((prev) => ({ ...prev, folderId: e.target.value }))} disabled={!form.planId}>
                              <option value="">Racine du plan</option>
                              {filteredFolders.map((folder) => <option key={folder.id} value={folder.id}>{folder.label}</option>)}
                            </select>
                            <small className="archive-create-hint">Sélectionnez un dossier uniquement si nécessaire.</small>
                          </div>
                          <div className="col-md-6 form-group">
                            <label htmlFor="archive-rule">Règle de conservation <span className="archive-create-required">*</span></label>
                            <select id="archive-rule" className="form-control" value={form.regleConservationId}
                              onChange={(e) => { setForm((prev) => ({ ...prev, regleConservationId: e.target.value })); setValidationErrors((prev) => ({ ...prev, regleConservationId: '' })); }}
                              disabled={regles.length === 0} aria-invalid={!!validationErrors.regleConservationId}>
                              <option value="">{regles.length === 0 ? 'Aucune règle disponible' : 'Choisir une règle'}</option>
                              {regles.filter((regle) => String(regle.id) === String(form.regleConservationId) || !regles.some((candidate) => Number(candidate.supersedesRuleId) === Number(regle.id))).map((regle) => <option key={regle.id} value={regle.id}>{regle.code ? `${regle.code} — ` : ''}{regle.regle} · v{regle.version || 1}</option>)}
                            </select>
                            {validationErrors.regleConservationId && <div className="archive-create-error" role="alert">{validationErrors.regleConservationId}</div>}
                          </div>
                          {regles.find((item) => String(item.id) === String(form.regleConservationId))?.retentionTrigger === 'event' && <div className="col-md-6 form-group">
                            <label htmlFor="archive-retention-event">Date de l’événement déclencheur <span className="archive-create-required">*</span></label>
                            <input id="archive-retention-event" type="date" className="form-control" value={form.retentionEventDate} onChange={(e) => setForm((prev) => ({ ...prev, retentionEventDate: e.target.value }))} required />
                            <small className="archive-create-hint">{regles.find((item) => String(item.id) === String(form.regleConservationId))?.triggerEventLabel}</small>
                            {validationErrors.retentionEventDate && <div className="archive-create-error" role="alert">{validationErrors.retentionEventDate}</div>}
                          </div>}
                        </div>
                      </div>
                    </section>

                    <section id="archive-create-verification" className="archive-create-section">
                      <div className="archive-create-section-heading">
                        <span className="archive-create-step">3</span>
                        <div><h5>Gestion des métadonnées</h5><p>Choisissez le profil, lancez l’OCR et contrôlez les métadonnées.</p></div>
                      </div>
                      <div className="archive-create-section-body">
                        <div className="form-group">
                          <label htmlFor="archive-profile">Profil d'archive <span className="archive-create-required">*</span></label>
                          <select id="archive-profile" className="form-control" value={form.archiveProfileId}
                            onChange={(e) => chooseProfile(e.target.value)} disabled={profiles.length === 0}
                            aria-invalid={!!validationErrors.archiveProfileId}>
                            <option value="">{profiles.length === 0 ? 'Aucun profil disponible' : 'Choisir un profil'}</option>
                            {profiles.map((item) => <option key={item.id} value={item.id}>{item.code} - {item.title}</option>)}
                          </select>
                          {validationErrors.archiveProfileId && <div className="archive-create-error" role="alert">{validationErrors.archiveProfileId}</div>}
                        </div>
                        {captureFields.length > 0 && (
                          <div className="archive-create-ocr">
                            <div><strong>Capture OCR</strong><p>Analysez le fichier pour préremplir les métadonnées du profil.</p></div>
                            <button className="btn btn-outline-primary" type="button" onClick={handlePreviewOcr}
                              disabled={ocrPreviewLoading || !archiveFile || !form.archiveProfileId}>
                              {ocrPreviewLoading ? 'Analyse en cours...' : 'Analyser le document'}
                            </button>
                            {ocrPreviewError && <div className="archive-create-error" role="alert">{ocrPreviewError}</div>}
                            {ocrPreview && Object.keys(ocrPreview).length === 0 && <div className="archive-create-hint">Aucune valeur détectée.</div>}
                          </div>
                        )}
                        <div className="archive-create-metadata-accordion">
                          <button type="button" className="archive-create-metadata-toggle"
                            aria-expanded={showArchiveMetadata} aria-controls="archive-create-metadata-content"
                            onClick={() => setShowArchiveMetadata((previous) => !previous)}>
                            <span>Métadonnées de l’archive</span>
                            <span className="archive-create-metadata-symbol" aria-hidden="true">{showArchiveMetadata ? '−' : '+'}</span>
                          </button>
                          <div id="archive-create-metadata-content" className="archive-create-metadata-content" hidden={!showArchiveMetadata}>
                            {captureFields.length > 0 && <MetadataReview
                              fields={captureFields} values={ocrOverrides} logs={captureLogs}
                              file={archiveFile} errors={validationErrors} reviewed={reviewedMetadata} analyzed={!!ocrPreview}
                              onChange={(key, value) => {
                                protectedMetadata.current[key] = true;
                                setOcrOverrides((prev) => ({ ...prev, [key]: value }));
                                setReviewedMetadata((prev) => ({ ...prev, [key]: false }));
                                setValidationErrors((prev) => ({ ...prev, [`metadata:${key}`]: '' }));
                              }}
                              onValidate={(key) => {
                                protectedMetadata.current[key] = true;
                                setReviewedMetadata((prev) => ({ ...prev, [key]: true }));
                              }}
                            />}
                            <div className="archive-create-advanced">
                              <button type="button" className="archive-create-advanced-toggle" onClick={() => setShowAdvanced((previous) => !previous)}
                                aria-expanded={showAdvanced} aria-controls="archive-create-advanced-content">
                                Ajouter une métadonnée <span aria-hidden="true">{showAdvanced ? '−' : '+'}</span>
                              </button>
                              {showAdvanced && (
                                <div id="archive-create-advanced-content" className="archive-create-advanced-content">
                                  <div>
                                    <h6>Métadonnées personnalisées</h6>
                                    {customMetadataFields.map((field, index) => (
                                      <div className="row archive-create-custom-row" key={`custom-meta-${index}`}>
                                      <div className="col-md-5"><input className="form-control" aria-label="Nom de la métadonnée" placeholder="Nom de la métadonnée" value={field.key}
                                        onChange={(e) => updateCustomMetadataField(index, 'key', e.target.value)} /></div>
                                      <div className="col-md-5"><input className="form-control" aria-label="Valeur à ajouter" placeholder="Valeur à ajouter" value={field.value}
                                        onChange={(e) => updateCustomMetadataField(index, 'value', e.target.value)} /></div>
                                      <div className="col-md-2"><button className="btn btn-outline-danger btn-sm" type="button"
                                        onClick={() => removeCustomMetadataField(index)}>Supprimer</button></div>
                                        </div>
                                      ))}
                                      <button className="btn btn-outline-primary btn-sm" type="button" onClick={addCustomMetadataField}>Ajouter une métadonnée</button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="archive-create-summary">
                          <strong>Récapitulatif</strong>
                          <dl>
                            <div><dt>Document</dt><dd>{archiveFile?.name || docInfo?.latestVersion?.originalName || 'À ajouter'}</dd></div>
                            <div><dt>Titre</dt><dd>{form.title || 'À renseigner'}</dd></div>
                            <div><dt>Profil</dt><dd>{selectedProfile ? `${selectedProfile.code} - ${selectedProfile.title}` : 'À choisir'}</dd></div>
                            <div><dt>Service</dt><dd>{addingService ? newService.title || 'Nouveau service à renseigner' : services.find((item) => String(item.id) === String(form.producerServiceId))?.label || 'À choisir'}</dd></div>
                            <div><dt>Destination</dt><dd>{filteredFolders.find((item) => String(item.id) === String(form.folderId))?.label || availablePlans.find((item) => String(item.id) === String(form.planId))?.title || 'À choisir'}</dd></div>
                            <div><dt>Règle de conservation</dt><dd>{regles.find((item) => String(item.id) === String(form.regleConservationId))?.regle || 'À choisir'}</dd></div>
                            {form.reference.trim() && <div><dt>Référence</dt><dd>{form.reference.trim()}</dd></div>}
                          </dl>
                        </div>
                      </div>
                    </section>

                    <div className="archive-create-actions">
                      <span>{!canCreateArchive ? 'Accès insuffisant pour créer une archive sur ce plan.' : '* Champs obligatoires'}</span>
                      <button className="btn btn-primary" type="submit" disabled={saving || !canCreateArchive}>
                        {saving ? 'Traitement...' : isEdit ? 'Mettre à jour' : 'Créer l’archive'}
                      </button>
                    </div>
                  </form>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ArchiveCreate;
